import { useEffect, useState, useMemo } from "react";
import Select from "react-select"
import DatePicker from "react-datepicker";
import moment from "moment";
import { AuthService } from "../../Services/authServices";
import toast from "react-hot-toast";
import { defaultTheme, NxField, NxButton, NxIcon } from "../nx";
import { State, City } from "country-state-city";
import {
    DocItem,
    MAX_QUALIFICATION_DOCS,
    QualificationDocsPicker,
    docItemsFromProfile,
} from "../QualificationDocs/QualificationDocs";


interface IEditUserInfoModal {
    show: any,
    handleClose: any,
    data: any,
    reload: any
}

const t = defaultTheme;

export default function EditUserInfoModal({ show, handleClose, data, reload }: IEditUserInfoModal) {


    const [userData, setUserData] = useState<any>();
    const [errors, setErrors] = useState<Record<string, string>>({});

    const stateOptions = useMemo(() => State.getStatesOfCountry("IN").map(s => ({ value: s.isoCode, label: s.name })), []);
    const cityOptions = useMemo(() => {
        if (!userData?.stateCode) return [];
        return City.getCitiesOfState("IN", userData.stateCode).map((c: any) => ({ value: c.name, label: c.name }));
    }, [userData?.stateCode]);

    const handleChangeValue = (e: any) => {
        const { name, value } = e.target;
        if (name === "mobile") {
            const cleaned = value.replace(/\D/g, "");
            if (cleaned.length > 10) return;
            setUserData({ ...userData, mobile: cleaned });
            if (errors.mobile) setErrors(prev => ({ ...prev, mobile: "" }));
            return;
        }
        setUserData({ ...userData, [name]: value });
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    }


    const [uploading, setUploading] = useState(false);
    const [qualDocs, setQualDocs] = useState<DocItem[]>([]);

    const uploadFile = async (file: File): Promise<string> => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await AuthService.uploadFile(fd);
        return res.data?.url || res.data?.data?.fileUrl || res.data;
    };

    const handleImageUpload = async (file: File) => {
        try {
            setUploading(true);
            const imageUrl = await uploadFile(file);
            setUserData((prev: any) => ({ ...prev, image: imageUrl }));
        } catch {
            toast.error("Failed to upload image");
        } finally {
            setUploading(false);
        }
    };

    const validate = (): Record<string, string> => {
        const e: Record<string, string> = {};
        if (!userData?.name?.trim()) e.name = "First name is required";
        if (!userData?.last_name?.trim()) e.last_name = "Last name is required";
        if (!userData?.dob) e.dob = "Date of birth is required";
        else {
            const dob = new Date(userData.dob);
            const today = new Date();
            if (dob > today) e.dob = "Date of birth cannot be in the future";
            else {
                const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                if (age < 18) e.dob = "You must be at least 18 years old";
            }
        }
        if (!userData?.gender) e.gender = "Please select a gender";
        if (!userData?.mobile?.trim()) e.mobile = "Mobile number is required";
        else if (!/^\d{10}$/.test(userData.mobile.trim())) e.mobile = "Mobile number must be exactly 10 digits";
        if (!userData?.city?.trim()) e.city = "City is required";
        if (!userData?.state?.trim()) e.state = "State is required";
        return e;
    };

    const updateUser = async () => {
        const fieldErrors = validate();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            toast.error("Please fix the highlighted fields");
            return;
        }
        setErrors({});

        try {
            setUploading(true);

            const savedDocs = [];
            for (const doc of qualDocs) {
                const url = doc.file ? await uploadFile(doc.file) : doc.url;
                savedDocs.push({ url, name: doc.name, size: doc.size });
            }

            const payload: any = {
                name: userData?.name,
                last_name: userData?.last_name,
                dob: userData?.dob,
                gender: userData?.gender,
                mobile: userData?.mobile,
                image: userData?.image,
                city: userData?.city,
                state: userData?.state,
                qualification: userData?.qualification,
                isIriaMember: userData?.isIriaMember,
                membershipNumber: userData?.membershipNumber,
                qualificationDocs: savedDocs,
            };

            const res = await AuthService.updateuser(payload);
            if (res.status === 200) {
                toast.success("User details updated successfully");
                handleClose();
                reload();
            }
        } catch {
            toast.error("Unable to update details");
        } finally {
            setUploading(false);
        }
    }

    useEffect(() => {
        if (data) {
            const matchedState = data.state
                ? State.getStatesOfCountry("IN").find(s => s.name === data.state)
                : null;
            setUserData({ ...data, stateCode: matchedState?.isoCode || "" });
            setQualDocs(docItemsFromProfile(data.qualificationDocs, data.qualificationDoc));
        }
    }, [data])

    const labelStyle: React.CSSProperties = {
        display: 'block', fontSize: 11, fontWeight: 500,
        letterSpacing: '0.04em', textTransform: 'uppercase',
        color: t.inkMute, marginBottom: 6,
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', height: 44, padding: '0 14px',
        boxSizing: 'border-box', background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: 8, fontFamily: 'inherit', fontSize: 14,
        color: t.ink, outline: 'none',
    };

    // Style react-select to match the nx theme
    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: 44,
            background: t.surface,
            borderColor: state.isFocused ? t.borderStrong : t.border,
            borderRadius: 8,
            boxShadow: 'none',
            fontSize: 14,
            '&:hover': { borderColor: t.borderStrong },
        }),
        menu: (base: any) => ({ ...base, fontSize: 14, borderRadius: 8, overflow: 'hidden', zIndex: 20 }),
        option: (base: any, state: any) => ({
            ...base,
            background: state.isSelected ? t.accent : state.isFocused ? t.paperAlt : t.surface,
            color: state.isSelected ? t.accentInk : t.ink,
        }),
        singleValue: (base: any) => ({ ...base, color: t.ink }),
    };

    return (
        <>
            {show && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9998,
                        background: 'rgba(0,0,0,0.4)',
                        fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
                    }}
                    onClick={handleClose}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'absolute', top: 0, right: 0, bottom: 0,
                            width: '100%', maxWidth: 420,
                            background: t.surface, borderLeft: `1px solid ${t.border}`,
                            display: 'flex', flexDirection: 'column',
                            boxShadow: '-12px 0 40px rgba(0,0,0,0.12)',
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Edit Details</h3>
                            <button
                                onClick={handleClose}
                                style={{
                                    background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                    padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >Close</button>
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <NxField
                                    t={t}
                                    label="First Name"
                                    name="name"
                                    value={userData?.name || ''}
                                    onChange={handleChangeValue}
                                />
                                <NxField
                                    t={t}
                                    label="Last Name"
                                    name="last_name"
                                    value={userData?.last_name || ''}
                                    onChange={handleChangeValue}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>Email</label>
                                <input
                                    style={{ ...inputStyle, background: t.paperAlt, color: t.inkMute, cursor: 'not-allowed' }}
                                    value={userData?.email || ''}
                                    disabled
                                />
                                <div style={{ fontSize: 11, color: t.inkMute, marginTop: 4 }}>Email cannot be changed</div>
                            </div>

                            <div>
                                <label style={labelStyle}>Date of Birth</label>
                                <DatePicker
                                    selected={userData?.dob ? moment(userData.dob).toDate() : null}
                                    onChange={(date: any) => {
                                        setUserData({ ...userData, dob: date });
                                        if (errors.dob) setErrors(prev => ({ ...prev, dob: "" }));
                                    }}
                                    name="dob"
                                    dateFormat="dd MMM yyyy"
                                    maxDate={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate())}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    wrapperClassName="d-block"
                                    customInput={<input style={{ ...inputStyle, borderColor: errors.dob ? t.danger : t.border }} />}
                                />
                                {errors.dob && <div style={{ fontSize: 12, color: t.danger, marginTop: 4 }}>{errors.dob}</div>}
                            </div>

                            <div>
                                <label style={labelStyle}>Gender</label>
                                <Select
                                    options={[
                                        { label: "Male", value: "male" },
                                        { label: "Female", value: "female" },
                                        { label: "Other", value: "other" },
                                    ]}
                                    value={userData?.gender ? { label: userData.gender.charAt(0).toUpperCase() + userData.gender.slice(1), value: userData.gender } : null}
                                    onChange={(e: any) => setUserData({ ...userData, gender: e.value })}
                                    styles={selectStyles}
                                />
                            </div>

                            <NxField
                                t={t}
                                label="Mobile (WhatsApp)"
                                name="mobile"
                                value={userData?.mobile || ''}
                                onChange={handleChangeValue}
                                error={!!errors.mobile}
                                helper={errors.mobile}
                                placeholder="10 digit mobile number"
                            />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div>
                                    <label style={labelStyle}>State</label>
                                    <Select
                                        options={stateOptions}
                                        value={userData?.stateCode ? { value: userData.stateCode, label: userData.state } : null}
                                        onChange={(opt: any) => {
                                            setUserData({ ...userData, state: opt?.label || "", stateCode: opt?.value || "", city: "" });
                                            if (errors.state) setErrors(prev => ({ ...prev, state: "" }));
                                        }}
                                        placeholder="Select state..."
                                        isClearable
                                        styles={selectStyles}
                                    />
                                    {errors.state && <div style={{ fontSize: 12, color: t.danger, marginTop: 4 }}>{errors.state}</div>}
                                </div>
                                <div>
                                    <label style={labelStyle}>City</label>
                                    <Select
                                        options={cityOptions}
                                        value={userData?.city ? { value: userData.city, label: userData.city } : null}
                                        onChange={(opt: any) => {
                                            setUserData({ ...userData, city: opt?.value || "" });
                                            if (errors.city) setErrors(prev => ({ ...prev, city: "" }));
                                        }}
                                        placeholder={userData?.stateCode ? "Select city..." : "Select state first"}
                                        isDisabled={!userData?.stateCode}
                                        isClearable
                                        styles={selectStyles}
                                    />
                                    {errors.city && <div style={{ fontSize: 12, color: t.danger, marginTop: 4 }}>{errors.city}</div>}
                                </div>
                            </div>

                            <NxField
                                t={t}
                                label="Qualification"
                                name="qualification"
                                value={userData?.qualification || ''}
                                onChange={handleChangeValue}
                            />

                            <div>
                                <label style={labelStyle}>IRIA Member</label>
                                <Select
                                    options={[
                                        { label: "No", value: false },
                                        { label: "Yes", value: true },
                                    ]}
                                    value={userData?.isIriaMember ? { label: "Yes", value: true } : { label: "No", value: false }}
                                    onChange={(e: any) => setUserData({ ...userData, isIriaMember: e.value })}
                                    styles={selectStyles}
                                />
                            </div>

                            {userData?.isIriaMember && (
                                <NxField
                                    t={t}
                                    label="Membership Number"
                                    name="membershipNumber"
                                    value={userData?.membershipNumber || ''}
                                    onChange={handleChangeValue}
                                />
                            )}

                            <div>
                                <label style={labelStyle}>Profile Image</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <img
                                        src={userData?.image || "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png"}
                                        alt="Preview"
                                        style={{
                                            width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                                            border: `1px solid ${t.border}`, flexShrink: 0,
                                        }}
                                    />
                                    <label style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        height: 40, padding: '0 16px', borderRadius: 8,
                                        border: `1px solid ${t.borderStrong}`, background: 'transparent', color: t.ink,
                                        fontSize: 13, fontWeight: 500, cursor: uploading ? 'wait' : 'pointer',
                                        opacity: uploading ? 0.6 : 1,
                                    }}>
                                        {NxIcon.upload(t.ink)}
                                        {uploading ? 'Uploading...' : 'Upload'}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            name="image"
                                            disabled={uploading}
                                            onChange={(e: any) => {
                                                if (e.target.files?.[0]) handleImageUpload(e.target.files[0]);
                                            }}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label style={labelStyle}>Qualification Documents (up to {MAX_QUALIFICATION_DOCS})</label>
                                <QualificationDocsPicker
                                    t={t}
                                    docs={qualDocs}
                                    onChange={setQualDocs}
                                    max={MAX_QUALIFICATION_DOCS}
                                    disabled={uploading}
                                    inputId="profile-qualification-docs-upload"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.border}` }}>
                            <NxButton t={t} kind="primary" w="100%" onClick={updateUser} disabled={uploading}>
                                {uploading ? 'Saving...' : 'Save Changes'}
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
